<template>
  <div class="venue-map-editor">
    <div class="editor-toolbar">
      <label class="upload-btn">
        <input type="file" accept="image/*" @change="onUploadImage" hidden />
        上传平面图
      </label>
      <label class="upload-btn">
        <input type="file" accept=".json,application/json" @change="onImportPolygon" hidden />
        导入 polygon JSON
      </label>
      <button type="button" class="btn" :disabled="!layout.backgroundUrl && !pendingBackground" @click="startDraw">
        {{ drawing ? '取消绘制' : '绘制区域' }}
      </button>
      <button type="button" class="btn primary" :disabled="!drawing || draftPoints.length < 3" @click="finishDraw">
        完成当前多边形
      </button>
      <button type="button" class="btn danger" :disabled="!selectedRegionId" @click="deleteRegion">
        删除选中区域
      </button>
      <button type="button" class="btn primary" @click="saveLayout" :disabled="saving">
        {{ saving ? '保存中…' : '保存地图配置' }}
      </button>
      <span v-if="saveMessage" class="save-msg">{{ saveMessage }}</span>
    </div>

    <div v-if="drawing && draftPoints.length" class="region-form">
      <label>
        区域 ID（对应数据中的 area 字段）
        <input v-model="newRegionId" list="area-suggestions" placeholder="如 cl1、cr2" />
        <datalist id="area-suggestions">
          <option v-for="a in knownAreas" :key="a" :value="a" />
        </datalist>
      </label>
      <label>
        显示名称
        <input v-model="newRegionLabel" placeholder="可选" />
      </label>
      <label>
        所属舞台
        <select v-model="newRegionStage">
          <option value="">未指定</option>
          <option v-for="s in stages" :key="s.id" :value="s.id">{{ s.name }}</option>
        </select>
      </label>
      <p class="hint">在图上依次点击顶点，至少 3 个点后点「完成当前多边形」</p>
    </div>

    <VenueMapCanvas
      :layout="displayLayout"
      :editable="drawing"
      :draft-points="draftPoints"
      :selected-region-ids="selectedRegionId ? [selectedRegionId] : []"
      :show-counts="false"
      @canvas-click="addDraftPoint"
      @region-click="selectRegion"
    />

    <div class="regions-list" v-if="layout.regions.length">
      <h4>已标记区域 ({{ layout.regions.length }})</h4>
      <ul>
        <li
          v-for="r in layout.regions"
          :key="r.id"
          :class="{ active: selectedRegionId === r.id }"
          @click="selectedRegionId = r.id"
        >
          <strong>{{ getRegionDisplayName(r) }}</strong>
          <span v-if="r.stage"> — {{ r.stage }}</span>
          <span class="pts">({{ r.points?.length || 0 }} 顶点)</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import api from '../api/config';
import VenueMapCanvas from './VenueMapCanvas.vue';
import { readImageFileAsDataUrl, loadImageDimensions, getRegionDisplayName } from '../utils/venueMap';

export default {
  name: 'VenueMapEditor',
  components: { VenueMapCanvas },
  props: {
    layout: {
      type: Object,
      required: true,
    },
    stages: {
      type: Array,
      default: () => [],
    },
    knownAreas: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['saved', 'update:layout'],
  data() {
    return {
      drawing: false,
      draftPoints: [],
      selectedRegionId: null,
      newRegionId: '',
      newRegionLabel: '',
      newRegionStage: '',
      pendingBackground: null,
      pendingDimensions: null,
      saving: false,
      saveMessage: '',
    };
  },
  computed: {
    displayLayout() {
      if (this.pendingBackground) {
        return {
          ...this.layout,
          backgroundUrl: this.pendingBackground,
          imageWidth: this.pendingDimensions?.width || this.layout.imageWidth,
          imageHeight: this.pendingDimensions?.height || this.layout.imageHeight,
        };
      }
      return this.layout;
    },
  },
  methods: {
    getRegionDisplayName,
    startDraw() {
      this.drawing = !this.drawing;
      if (!this.drawing) {
        this.draftPoints = [];
      }
    },
    addDraftPoint(point) {
      if (!this.drawing) return;
      this.draftPoints = [...this.draftPoints, point];
    },
    finishDraw() {
      if (this.draftPoints.length < 3) return;
      const id = (this.newRegionId || `region_${Date.now()}`).trim();
      if (!id) {
        alert('请填写区域 ID');
        return;
      }
      const region = {
        id,
        label: this.newRegionLabel.trim(),
        stage: this.newRegionStage || undefined,
        points: [...this.draftPoints],
      };
      const regions = [...this.layout.regions.filter(r => r.id !== id), region];
      this.$emit('update:layout', {
        ...this.layout,
        imageWidth: this.displayLayout.imageWidth,
        imageHeight: this.displayLayout.imageHeight,
        regions,
      });
      this.draftPoints = [];
      this.drawing = false;
      this.newRegionId = '';
      this.newRegionLabel = '';
      this.selectedRegionId = id;
    },
    selectRegion(region) {
      this.selectedRegionId = region.id;
      this.newRegionId = region.id;
      this.newRegionLabel = region.label || '';
      this.newRegionStage = region.stage || '';
    },
    deleteRegion() {
      if (!this.selectedRegionId) return;
      const regions = this.layout.regions.filter(r => r.id !== this.selectedRegionId);
      this.$emit('update:layout', { ...this.layout, regions });
      this.selectedRegionId = null;
    },
    async onImportPolygon(evt) {
      const file = evt.target.files?.[0];
      if (!file) return;
      try {
        const text = await file.text();
        const points = JSON.parse(text);
        if (!Array.isArray(points) || points.length < 3) {
          alert('JSON 需为至少 3 个顶点的数组，格式：[[x,y], ...]（与 video-processing polygon 输出一致）');
          return;
        }
        const id = (this.newRegionId || file.name.replace(/\.json$/i, '')).trim();
        const region = {
          id,
          label: this.newRegionLabel.trim(),
          stage: this.newRegionStage || undefined,
          points,
        };
        const regions = [...this.layout.regions.filter(r => r.id !== id), region];
        this.$emit('update:layout', { ...this.layout, regions });
        this.selectedRegionId = id;
        this.saveMessage = '已导入多边形，请保存配置';
      } catch (e) {
        console.error(e);
        alert('导入失败，请检查 JSON 格式');
      }
      evt.target.value = '';
    },
    async onUploadImage(evt) {
      const file = evt.target.files?.[0];
      if (!file) return;
      try {
        const dataUrl = await readImageFileAsDataUrl(file);
        const dims = await loadImageDimensions(dataUrl);
        this.pendingBackground = dataUrl;
        this.pendingDimensions = dims;
        this.$emit('update:layout', {
          ...this.layout,
          imageWidth: dims.width,
          imageHeight: dims.height,
        });
        await api.post('/venue-layout/background', {
          imageData: dataUrl,
          mimeType: file.type,
        });
        this.saveMessage = '底图已上传';
        this.pendingBackground = null;
        this.$emit('saved');
      } catch (e) {
        console.error(e);
        alert('上传失败，请重试');
      }
      evt.target.value = '';
    },
    async saveLayout() {
      this.saving = true;
      this.saveMessage = '';
      try {
        await api.put('/venue-layout', { layout: this.layout });
        this.saveMessage = '已保存';
        this.$emit('saved');
      } catch (e) {
        console.error(e);
        this.saveMessage = '保存失败';
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.venue-map-editor {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.editor-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.upload-btn,
.btn {
  padding: 8px 14px;
  border-radius: 6px;
  border: 1px solid #d9d9d9;
  background: #fff;
  cursor: pointer;
  font-size: 14px;
}

.upload-btn:hover,
.btn:hover:not(:disabled) {
  border-color: #1677ff;
  color: #1677ff;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn.primary {
  background: #1677ff;
  color: #fff;
  border-color: #1677ff;
}

.btn.danger {
  color: #cf1322;
  border-color: #ffa39e;
}

.region-form {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px;
  background: #f5f8ff;
  border-radius: 8px;
}

.region-form label {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
  min-width: 160px;
}

.region-form input,
.region-form select {
  padding: 6px 8px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
}

.hint {
  width: 100%;
  margin: 0;
  font-size: 12px;
  color: #666;
}

.regions-list ul {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.regions-list li {
  padding: 6px 12px;
  background: #f0f0f0;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
}

.regions-list li.active {
  background: #e6f4ff;
  border: 1px solid #91caff;
}

.pts {
  color: #888;
  font-size: 12px;
}

.save-msg {
  font-size: 13px;
  color: #52c41a;
}
</style>
